import { useDispatch, useSelector } from 'react-redux';
import { Stack, useMediaQuery } from '@mui/material';

import { AppDispatch, RootState } from 'redux/store';
import CardWrapper from 'components/Common/CardWrapper';
import { useLocation } from 'react-router-dom';
import ProfileItemDetail from 'components/ProfileItemDetail';
import { useEffect, useState } from 'react';
import { fetchSecretInfo, generateSecretKey, updateNotificationEmail } from 'redux/slices/user/secretInfoSlice';
import InfoModal from 'components/Common/InfoModal';
import { Controller, useForm } from 'react-hook-form';
import TextInput from 'components/Common/TextInput';
import { notify } from 'components/Common/Toast';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomButton from 'components/Common/CustomButton';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { handleCopyClipBoard, showCopiedToast } from 'utils/helpers';
import MuiLoader from 'components/Common/Loader';

const UPDATE_EMAIL = 'updateEmail';
const GENERATE_KEY = 'generateKey';

const SecretKeySetting = () => {
    const location = useLocation() || {};
    const { showButton } = location?.state || {};
    const { secretInfo, loading: secretDataLoading } = useSelector((state: RootState) => state.secretInfo) || {};
    const { secretKey, email: notificationEmail } = secretInfo;
    const [loading, setLoading] = useState<boolean>();
    const [modalOpen, setModalOpen] = useState<string | null>(null);
    const modalDescription =
        modalOpen === UPDATE_EMAIL
            ? 'Are you sure you want to add or update the email?'
            : 'Are you sure you want to generate a new webhook key?';

    const modalTitle = modalOpen === UPDATE_EMAIL ? 'Webhook Failure Notification Email' : 'Generate Key';
    const isLargeScreen = useMediaQuery('(min-width:1420px)');
    const isEmailFieldVisible = (!notificationEmail && modalOpen === GENERATE_KEY) || modalOpen === UPDATE_EMAIL;

    const formSchema = yup.object({
        email: isEmailFieldVisible
            ? yup.string().required('Email is required.').email('Invalid email format.').lowercase().trim()
            : yup.string().notRequired(),
    });
    const {
        handleSubmit,
        getValues,
        control,
        reset,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(formSchema),
        defaultValues: { email: '' },
    });
    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        dispatch(fetchSecretInfo());
    }, [dispatch]);

    const handleClickConfirm = async () => {
        let { email } = getValues();
        setLoading(true);
        if (!isEmailFieldVisible) {
            email = notificationEmail;
        }
        try {
            let response: any = null;

            if (modalOpen === UPDATE_EMAIL) {
                response = await dispatch(updateNotificationEmail(email));
                const { code } = response?.payload || {};
                if (code === 200) {
                    if (notificationEmail) {
                        notify('Notification email has been updated successfully.', 'success');
                    } else {
                        notify('Notification email has been added successfully.', 'success');
                    }
                }
                return;
            }

            response = await dispatch(generateSecretKey(email));
            const { code } = response?.payload || {};
            if (code === 200) {
                notify('Webhook key has been generated successfully.', 'success');
            }
        } catch (e) {
            console.log('An error occurred. Please try again.');
        } finally {
            setLoading(false);
            setModalOpen(null);
            reset();
        }
    };

    const handelCancel = () => {
        setModalOpen(null);
        reset();
    };

    if (secretDataLoading && !secretKey && !notificationEmail) {
        return <MuiLoader absolute loading={secretDataLoading} />;
    }

    const handelCopyText = () => {
        handleCopyClipBoard(secretKey);
        showCopiedToast('Secret key copied!');
    };
    return (
        <>
            <CardWrapper hideButton={!showButton}>
                <Stack mx={'15vh'}>
                    <Stack mb={5}>
                        <ProfileItemDetail
                            icon={<BorderColorIcon />}
                            handelIconButtonClick={() => setModalOpen(UPDATE_EMAIL)}
                            label="Webhook Failure Notification Email"
                            value={notificationEmail || 'Click the edit button to add your email.'}
                        />
                        <ProfileItemDetail
                            disabledIcon={!secretKey}
                            handelIconButtonClick={handelCopyText}
                            icon={<ContentCopyRoundedIcon />}
                            label="Webhook Key"
                            value={secretKey || 'Generate Key'}
                        />
                    </Stack>
                    <Stack alignItems={'center'}>
                        <CustomButton
                            title={secretKey ? 'Generate new webhook key' : 'Generate webhook key'}
                            sx={{
                                width: isLargeScreen ? '50%' : '90%',
                                fontSize: isLargeScreen ? '18px' : '16px',
                            }}
                            onClick={() => setModalOpen(GENERATE_KEY)}
                        />
                    </Stack>
                    <InfoModal
                        loading={loading}
                        description={modalDescription}
                        isOpen={!!modalOpen}
                        onClose={handelCancel}
                        title={modalTitle}
                        onConfirm={handleSubmit(handleClickConfirm)}
                    >
                        <>
                            {isEmailFieldVisible && (
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            sx={{ mt: 2, mb: 2 }}
                                            width="100%"
                                            label="Email"
                                            icon={null}
                                            error={!!errors?.email}
                                            helperText={(errors.email as any)?.message}
                                        />
                                    )}
                                />
                            )}
                        </>
                    </InfoModal>
                </Stack>
            </CardWrapper>
        </>
    );
};

export default SecretKeySetting;
